import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'px-2 py-1 text-xs rounded-lg',
  md: 'px-3 py-2 text-sm rounded-lg',
  lg: 'px-4 py-3 text-sm rounded-2xl',
};

export default function CustomSelect({
  value,
  options,
  onChange,
  className = '',
  placeholder,
  disabled = false,
  size = 'md',
}: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (!open) return;

    // 计算按钮位置，用于 Portal 定位
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);

    // ESC 关闭
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', keyHandler);

    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [open]);

  const selected = options.find(o => o.value === value);

  const dropdownContent = open ? (
    <div
      className="fixed z-[999999]"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
      }}
    >
      <div
        className="border border-white/10 rounded-xl shadow-2xl shadow-black/80 max-h-60 overflow-y-auto"
        style={{
          backgroundColor: '#16162a',
          colorScheme: 'dark',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              onChange(opt.value);
              setOpen(false);
            }}
            className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors ${
              opt.value === value
                ? 'text-cyan-300 bg-white/[0.08]'
                : 'text-white/70 hover:text-white hover:bg-white/[0.06]'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div ref={ref} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(!open)}
          className={`w-full bg-white/5 border border-white/10 ${sizeClasses[size]} font-bold text-left flex items-center justify-between cursor-pointer focus:border-white/30 outline-none transition-colors hover:bg-white/[0.07] disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
          style={{ color: selected ? undefined : 'rgba(255,255,255,0.45)' }}
        >
          <span style={{ color: selected ? 'white' : 'rgba(255,255,255,0.45)' }}>
            {selected ? selected.label : (placeholder || '选择...')}
          </span>
          <ChevronDown size={size === 'sm' ? 12 : 14} className={`text-white/45 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {/* 用 Portal 将下拉面板渲染到 body，避免父容器 overflow/stacking context 裁剪 */}
      {createPortal(dropdownContent, document.body)}
    </>
  );
}
